// src/pages/Deals/Deals.tsx
import React, { useState, useEffect, Fragment } from "react";
import parse from "html-react-parser";
import Slider, { Settings } from "react-slick";
import cx from "classnames";
import styles from "./styles.module.sass";
import Page from "../../layouts/Page";
import TextBlock from "../../components/TextBlock";
import Button from "../../components/Button";
import { hostname } from "../../constants";

interface Deal {
  id: number;
  content: { rendered: string };
  _embedded: {
    "wp:featuredmedia": Array<{
      media_details: { sizes: { full: { source_url: string } } };
    }>;
    "wp:term"?: Array<Array<{ slug: string }>>;
  };
}

interface PageACF {
  image_1: string;
  image_2: string;
  image_3: string;
}

const sliderSettings: Settings = {
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
};

const Deals: React.FC = () => {
  const [data, setData] = useState<Deal[]>([]);
  const [content, setContent] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [filtered, setFiltered] = useState<Deal[]>([]);
  const [page, setPage] = useState<PageACF | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetch(`${hostname}/wp-json/wp/v2/pages/73549`)
      .then((res) => res.json())
      .then((json: any) => setPage(json.acf))
      .catch(console.error);

    fetch(`${hostname}/wp-json/wp/v2/deal?_embed`)
      .then((res) => res.json())
      .then((deals: Deal[]) => {
        setData(deals);
        if (deals.length) setContent(deals[0].content.rendered);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedCity) return;
    const deals = data.filter((deal) =>
      deal._embedded["wp:term"]?.[0]?.[0]?.slug.includes(selectedCity)
    );
    setFiltered(deals);
    setContent(deals[0]?.content.rendered || "");
    setLoading(false);
  }, [selectedCity, data]);

  const handleAfterChange = (index: number) => {
    const deal = filtered[index];
    if (deal) setContent(deal.content.rendered);
  };

  const filterDeals = (city: string) => {
    setSelectedCity(city);
    setLoading(true);
  };

  return (
    <Page className={styles.base}>
      <div className={styles.filter}>
        <div className={styles.show}>Show deals in:</div>
        {["singapore", "bali", "kuala-lumpur"].map((city) => (
          <Button
            key={city}
            size="small"
            clickHandler={() => filterDeals(city)}
            className={cx({ [styles.isActive]: selectedCity === city })}
          >
            {city.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </Button>
        ))}
      </div>

      {loading && <div>Loading...</div>}

      {!loading && (
        <Fragment>
          {selectedCity ? (
            <Fragment key={selectedCity}>
              <Slider
                className={styles.slider}
                {...sliderSettings}
                afterChange={handleAfterChange}
              >
                {filtered.map((deal) => (
                  <div key={deal.id}>
                    <img
                      src={
                        deal._embedded["wp:featuredmedia"][0].media_details
                          .sizes.full.source_url
                      }
                      alt=""
                    />
                  </div>
                ))}
              </Slider>
              <TextBlock>{parse(content)}</TextBlock>
            </Fragment>
          ) : (
            page && (
              <div className={styles.cover}>
                {[page.image_1, page.image_2, page.image_3].map((src, i) => (
                  <div
                    key={i}
                    className={styles.imgWrapper}
                    onClick={() =>
                      filterDeals(["singapore", "bali", "kuala-lumpur"][i])
                    }
                  >
                    <img src={src} alt="" />
                  </div>
                ))}
              </div>
            )
          )}
        </Fragment>
      )}
    </Page>
  );
};

export default Deals;
